import { useQuery } from '@apollo/client';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { NoData, PageHeader, PrimaryButton } from '../../components';
import { GetStudentPapersQuery } from '../../generated/graphql';
import { academicYear, PageLoader } from '../../helpers';
import { useExamsTypeAndSem } from '../../helpers/examTypeAndSemester';
import { GET_STD_TIME_TABLE } from './graphql';
import {
  DetailItem,
  Details,
  PageWrapper,
  Row,
  Title,
  PageHeaderWrap,
  DetailItemkey,
  QRcode,
  PrintButton,
  TimetableWrap,
} from './styles';

/**
 * StudentHome
 *
 * Main dashboard for students.
 * - Fetches and displays the student's exam timetable.
 * - Allows printing of the timetable.
 * - Provides a link to report missing allocations.
 */
export const StudentHome = (): JSX.Element => {
  const navigate = useNavigate();
  // Ref for the timetable section to be printed
  const timetableRef = React.useRef(null);
  // Get exam type and semester info
  const examTypeAndSemester = useExamsTypeAndSem();

  // Setup print handler using react-to-print
  const handlePrint = useReactToPrint({
    content: () => timetableRef.current,
    documentTitle: `my-${academicYear}-${examTypeAndSemester?.examType}
    -${examTypeAndSemester?.semester}
    -exams-timetable`,
    pageStyle: 'lg:px-48 px-20',
    onPrintError: () => toast.error('Printing of timetable failed!'),
  });

  // Fetch the student's timetable data
  const { loading, data, error } = useQuery<GetStudentPapersQuery>(GET_STD_TIME_TABLE);

  // Show loader while fetching data
  if (loading) return <PageLoader />;

  // Handle error or no data found
  if (error || data?.getStudentPapers?.length === 0 || !data?.getStudentPapers) {
    toast.error(
      `Error occured in fetching your schedule, 
       please try and login in again or visit exams office`,
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      },
    );

    return (
      <div className="lg:mt-10 h-screen flex flex-col justify-center items-center">
        <NoData
          title="your schedule was not found"
          subtitle="Please visit the exams office to resolve this issue"
        />
        <Link className="text-blue-600 text-xl tracking-wide text-ellipsis underline" to="/">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <PageWrapper>
      {/* Set page title and meta info */}
      <Helmet>
        <title>KNUST, CoE Check timetable for Student</title>
        <meta
          name="check-timetable"
          content="Home page for KNUST,college of engineering students to check timetable schedule"
        />
      </Helmet>
      <PageHeaderWrap>
        <PageHeader />
      </PageHeaderWrap>

      {/* Info and link to report missing allocations */}
      <div
        className="text-center bg-white p-4
       mt-10 flex flex-col gap-3 justify-center items-center"
      >
        <h3>
          Fill out the forms below with your remaining courses if the courses displayed in your
          timetable are less than your registered courses
        </h3>
        <button
          onClick={(e) => {
            navigate('/student/report', {
              state: {
                indexNumber: data.getStudentPapers?.at(0)?.indexNumber,
                studentClass: data.getStudentPapers?.at(0)?.paper?.class,
              },
            });
          }}
        >
          <h4 className="text-blue-600 hover:border-b hover:border-blue-600">
            Click here to report allocation issues
          </h4>
        </button>
      </div>
      {/* Timetable display and print section */}
      <TimetableWrap ref={timetableRef}>
        <Title>
          {/* Exam type, semester, and academic year */}
          {/* eslint-disable-next-line max-len */}
          {examTypeAndSemester?.examType} EXAMINATION TIMETABLE FOR SEMESTER{' '}
          {examTypeAndSemester?.semester}, {academicYear} ACADEMIC YEAR
        </Title>
        <p className="text-center">
          Please print this schedule and return it to the Examination center for each session
        </p>
        {/* Render each paper/course row */}
        {data?.getStudentPapers?.map((student, index) => (
          <Row
            style={{
              marginBottom: index === 3 ? '24rem' : '',
              marginTop: index === 8 ? '24rem' : '',
            }}
            key={index}
          >
            <Details>
              <DetailItem>
                <DetailItemkey>Name</DetailItemkey> <span> : {student?.name}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Index Number</DetailItemkey> <span> : {student?.indexNumber}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Course</DetailItemkey>{' '}
                <span>
                  : {student?.paper?.courseCode} - {student?.paper?.courseName}
                </span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Time</DetailItemkey> <span>: {student?.paper?.time}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Date</DetailItemkey> <span>: {student?.paper?.date}</span>
              </DetailItem>
              {/* Room details: show "Computer Based" if not allocated, else show room info */}
              {!student?.allocatedRoom ? (
                <DetailItem>
                  <DetailItemkey>Room</DetailItemkey>
                  <span> : Computer Based</span>
                </DetailItem>
              ) : (
                <>
                  {student?.allocatedRoom?.name && (
                    <DetailItem>
                      <DetailItemkey>Room</DetailItemkey>
                      <span> : {student?.allocatedRoom?.name}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.college && (
                    <DetailItem>
                      <DetailItemkey>College</DetailItemkey>
                      <span> : {student?.allocatedRoom?.college}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.building && (
                    <DetailItem>
                      <DetailItemkey>Building</DetailItemkey>
                      <span> : {student?.allocatedRoom?.building}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.floor && (
                    <DetailItem>
                      <DetailItemkey>Floor</DetailItemkey>
                      <span> : {student?.allocatedRoom?.floor}</span>
                    </DetailItem>
                  )}
                </>
              )}
            </Details>
            {/* QR code for room, if available */}
            {student?.allocatedRoom?.qrCodeImageUrl && (
              <QRcode src={student.allocatedRoom.qrCodeImageUrl} />
            )}
          </Row>
        ))}
        {/* Add the keys to the floors here */}
      </TimetableWrap>
      {/* Print button for timetable */}
      <PrintButton onClick={handlePrint}>
        <PrimaryButton name="Print" iconClass="fas fa-print" />
      </PrintButton>

      {/* 
      // Optional: Logout button (currently commented out)
      <div className="w-full grid place-items-center mb-6">
        <button
          onClick={() => {
            navigate('/', { replace: true });
            localStorage.removeItem('coe-student-token');
          }}
        >
          <div className="bg-red-500 text-white w-24 py-2 rounded-md">
            <p>Logout</p>
          </div>
        </button>
      </div> 
      */}
    </PageWrapper>
  );
};
